import { CONFIG } from '../config';

type SentryMod = typeof import('./sentry');

let sentry: SentryMod | null = null;
const queue: Array<[unknown, Record<string, unknown> | undefined]> = [];
const recent = new Map<string, number>();

/** Identificador del tótem: ?totem=mall-plaza en la URL de inicio de Fully (se recuerda) */
export function deviceId(): string {
  let id: string | null = null;
  try {
    const fromUrl = new URLSearchParams(location.search).get('totem');
    if (fromUrl) localStorage.setItem('totem.id', fromUrl);
    id = localStorage.getItem('totem.id');
    if (!id) {
      id = `${CONFIG.sede === 'arequipa' ? 'totem' : CONFIG.sede}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem('totem.id', id);
    }
  } catch {
    id = 'totem-sin-storage';
  }
  return id;
}

/**
 * Reporta un error. Antes de que Sentry cargue (se importa diferido para no pesar en el
 * arranque) los errores se encolan. Mismo mensaje: como máximo una vez cada 10 minutos.
 */
export function report(err: unknown, context?: Record<string, unknown>) {
  const msg = err instanceof Error ? err.message : String(err);
  const now = Date.now();
  if ((recent.get(msg) || 0) > now - 10 * 60_000) return;
  recent.set(msg, now);
  console.error('[tótem]', msg, context || '');
  if (!CONFIG.sentryDsn) return;
  if (!sentry) {
    if (queue.length < 50) queue.push([err, context]);
    return;
  }
  sentry.captureException(err instanceof Error ? err : new Error(msg), { extra: context });
}

export async function initMonitoring(getTags: () => Record<string, string>) {
  window.addEventListener('error', (e) => report(e.error || e.message, { src: 'window.onerror' }));
  window.addEventListener('unhandledrejection', (e) => report(e.reason, { src: 'unhandledrejection' }));
  if (!CONFIG.sentryDsn) return;
  try {
    const S = await import('./sentry');
    S.init({
      dsn: CONFIG.sentryDsn,
      release: `totem@${CONFIG.appVersion}`,
      environment: import.meta.env.MODE,
      // Sin red los eventos se guardan en IndexedDB y se envían al volver la conexión
      transport: S.makeBrowserOfflineTransport(S.makeFetchTransport),
      tracesSampleRate: 0,
      maxBreadcrumbs: 40,
      defaultIntegrations: false,
      integrations: [S.breadcrumbsIntegration({ dom: false }), S.dedupeIntegration(), S.linkedErrorsIntegration()],
    });
    S.setTags({ device: deviceId(), ...getTags() });
    sentry = S;
    for (const [e, c] of queue.splice(0)) S.captureException(e instanceof Error ? e : new Error(String(e)), { extra: c });
  } catch (e) {
    console.warn('Sentry no cargó', e);
  }
}

export function setMonitorTag(key: string, value: string) {
  sentry?.setTag(key, value);
}
